/*eslint-disable no-unused-vars */
/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useState, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { Button, Form, Input, InputGroup, Label, Table } from 'reactstrap';
import { t } from 'i18next';
import SortableHeader from '../../components/SortableHeader';
import NoRecords from '../../components/Table/NoRecords';
import { PaginationControl } from 'react-bootstrap-pagination-control';
import ConfirmDeleteModal from '../ConfirmDeleteModal.js';
import { numberFormatter, formatDate } from '../../helpers';
import { ITEMS_PER_PAGE } from '../../constants';

const PAGE_SIZE_OPTIONS = [8, 10, 12, 15, 25];

function CashExpenseList({
  cashExpenseList,
  sortBy,
  handleSearch,
  removeCashExpense,
  editCashExpense,
  totalCount,
  currentPage,
  handlePageClick,
  itemsPerPage = ITEMS_PER_PAGE,
  onPageSizeChange,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCashExpenseDetail, setDeleteCashExpenseDetail] = useState({});
  const [localItemsPerPage, setLocalItemsPerPage] = useState(itemsPerPage);

  // Header list - removed location (since we're already in location tab)
  // Edit button column on right, delete button column on left (RTL)
  // Added EmployeeName column with sort
  const HeaderList = [
    {
      title: '',  // Edit button column (RIGHT side in RTL)
      value: 'editAction',
      style: { minWidth: '70px', width: '70px' },
      sortable: false,
    },
    {
      title: t('CASHEXPENSE.TABLE_DATE'),
      value: 'createDate',
      style: { minWidth: '100px', width: '10%' },
      sortable: true,
    },
    {
      title: t('CASHEXPENSE.TABLE_EXPENSE_NOTE'),
      value: 'expenseNote',
      style: { minWidth: '150px', width: '18%' },
      sortable: true,
    },
    {
      title: t('CASHEXPENSE.TABLE_PAID_TO'),
      value: 'paidTo',
      style: { minWidth: '120px', width: '12%' },
      sortable: true,
    },
    {
      title: t('CASHEXPENSE.TABLE_EXPENSE_AMOUNT'),
      value: 'expenseAmount',
      style: { minWidth: '100px', width: '10%' },
      sortable: true,
    },
    {
      title: t('CASHEXPENSE.TABLE_FROM_DATE'),
      value: 'fromDate',
      style: { minWidth: '100px', width: '10%' },
      sortable: true,
    },
    {
      title: t('CASHEXPENSE.TABLE_TO_DATE'),
      value: 'toDate',
      style: { minWidth: '100px', width: '10%' },
      sortable: true,
    },
    {
      title: t('CASHEXPENSE.TABLE_EMPLOYEE_NAME'),
      value: 'employeeName',
      style: { minWidth: '120px', width: '12%' },
      sortable: true,
    },
    {
      title: t('CASHEXPENSE.TABLE_INVOICE'),
      value: 'invoice',
      style: { minWidth: '80px', width: '8%' },
      sortable: true,
    },
    {
      title: '',  // Delete button column (LEFT side in RTL)
      value: 'deleteAction',
      style: { minWidth: '70px', width: '70px' },
      sortable: false,
    },
  ];

  const handleDeleteAction = (data) => {
    setShowDeleteModal(true);
    setDeleteCashExpenseDetail({
      ...data,
      ud: false,
    });
  };

  const handleConfirmDeleteAction = () => {
    removeCashExpense(deleteCashExpenseDetail);
    setShowDeleteModal(false);
  };

  const handleCancelAction = () => {
    setDeleteCashExpenseDetail({});
    setShowDeleteModal(false);
  };

  const debouncedChangeHandler = useCallback(debounce(handleSearch, 500), []);

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setLocalItemsPerPage(newSize);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Form className="search-filter" onSubmit={(e) => e.preventDefault()}>
          <InputGroup>
            <Input onChange={debouncedChangeHandler} placeholder={t('SEARCH')} />
            <Button color="primary" type="button">
              <i className="ri-search-line"></i>
            </Button>
          </InputGroup>
        </Form>
      </div>

      <Table striped bordered responsive size="sm">
        <thead>
          <tr>
            <SortableHeader 
              headers={HeaderList} 
              isDisabled={cashExpenseList.length === 0} 
              sortClick={sortBy}
              showActionCol={false}
            />
          </tr>
        </thead>

        <tbody>
          {cashExpenseList.map((item, l0Idx) => (
            <tr key={l0Idx}>
              {/* Edit button - RIGHT side in RTL */}
              <td className="text-center">
                <Button 
                  color="primary" 
                  size="sm" 
                  title={t('CASHEXPENSE.LIST_ACTION_EDIT')} 
                  onClick={() => editCashExpense(item)}
                >
                  {t('CASHEXPENSE.EDIT_BTN') || t('EDIT') || 'ערוך'}
                </Button>
              </td>
              <td>{formatDate(item.createdDate, 'dd/mm/yyyy')}</td>
              <td>{item.expenseNote}</td>
              <td>{item.paidTo}</td>
              <td>{numberFormatter(item.expenseAmount)}</td>
              <td>{formatDate(item.startDate, 'dd/mm/yyyy')}</td>
              <td>{formatDate(item.endDate, 'dd/mm/yyyy')}</td>
              <td>{item.employeeName || ''}</td>
              <td align="center">
                {item.invoice ? (
                  <i className="ri-check-fill text-success"></i>
                ) : (
                  <i className="ri-close-fill text-danger"></i>
                )}
              </td>
              {/* Delete button - LEFT side in RTL */}
              <td className="text-center">
                <Button 
                  color="danger" 
                  size="sm" 
                  title={t('CASHEXPENSE.LIST_ACTION_REMOVE')} 
                  onClick={() => handleDeleteAction(item)}
                >
                  {t('CASHEXPENSE.DELETE_BTN') || t('DELETE') || 'מחק'}
                </Button>
              </td>
            </tr>
          ))}

          {!isLoading && cashExpenseList.length === 0 && <NoRecords colSpan={HeaderList.length} />}
        </tbody>
      </Table>

      {/* Pagination and Rows per page */}
      <div className="custom-pagination mt-2 d-flex justify-content-between align-items-center flex-row-reverse">
        {/* Rows per page - RIGHT side in RTL */}
        <div className="d-flex align-items-center gap-2">
          <Label className="mb-0">{t('ROWS_PER_PAGE') || 'שורות לדף'}:</Label>
          <Input
            type="select"
            value={localItemsPerPage}
            onChange={handlePageSizeChange}
            style={{ width: '80px' }}
            bsSize="sm"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Input>
        </div>

        {/* Pagination - LEFT side in RTL */}
        <PaginationControl
          page={currentPage}
          total={totalCount}
          limit={localItemsPerPage}
          changePage={handlePageClick}
          next={true}
          last={true}
          ellipsis={1}
        />
      </div>

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        isLoading={isLoading}
        confirmFn={handleConfirmDeleteAction}
        cancelFn={handleCancelAction}
      />
    </React.Fragment>
  );
}

export default CashExpenseList;
